/* eslint-disable max-lines */
import {
  mapProductToResourceType,
  useTenantDetails,
  useUpdateAlarmGroupState,
  useUpdateLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  ABTestConfig,
  AIRecommendation,
  Escalation,
  Location,
  StatusType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  MonitoringIcon,
  SpecialZoneType,
  StatusStepper,
} from '@hakimo-ui/hakimo/ui-elements';
import {
  appendVersionParam,
  toast,
  useCanUpdateAlarmGroupStatus,
  useUser,
} from '@hakimo-ui/hakimo/util';
import { Button, LiveClock } from '@hakimo-ui/shared/ui-base';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useState } from 'react';
import MonitoringWindowInfo from '../../monitoring-window-info/MonitoringWindowInfo';
import ShareResource from '../../share-resource/ShareResource';
import { useAlarmGroupStatic } from '../alarm-provider/AlarmGroupProvider';
import { RESOLVED_STATUSES } from '../constants';
import { getProductConfig } from '../product-config';
import { CameraFeedMode, EscalationState } from '../types';
import { useAlarmGroupTracking } from '../useAlarmGroupTracking';
import {
  mapAlarmGroupStatusToLabelType,
  normalizeAlarmGroupStatus,
} from '../utils';
import { EscalationBannerContainer } from './EscalationBannerContainer';
import HeaderActions from './HeaderActions';
import MenuActions from './MenuActions';
import { ProgressiveLiveClock } from './ProgressiveLiveClock';
import { ProgressiveMonitoringWindow } from './ProgressiveMonitoringWindow';
import { ZoneOverlayDropdown } from './ZoneOverlayDropdown';

interface Props {
  location: Location;
  alarmGroupId: string;
  alarmStatus: string;
  globalCamMode: CameraFeedMode;
  onChangeGlobalCamMode: (mode: CameraFeedMode) => void;
  visibleZoneTypes: Set<SpecialZoneType>;
  onToggleVisibleZoneType: (zoneType: SpecialZoneType) => void;
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  escalation?: Escalation;
  scanEscalationState?: EscalationState;
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
  resolution?: string;
  product: UnifiedMonitoringProduct;
  showResolveAction?: boolean;
  addTemporaryResolvedState?: (alarmGroupId: string) => void;
  cameraNames?: string[];
  getDisplayStatus?: (alarmGroupId: string, actualStatus: string) => string;
  siblingEscalations?: EscalationState[];
  aiOperatorRep?: AIRecommendation;
  timestamp?: string;
  onAlarmResolve?: (status: StatusType) => void;
  onCloseAlarm?: () => void;
  showCloseAlarmButton?: boolean;
  abTestConfig?: ABTestConfig;
}

export function AlarmGroupHeader(props: Props) {
  const {
    location,
    alarmGroupId,
    alarmStatus,
    globalCamMode,
    onChangeGlobalCamMode,
    visibleZoneTypes,
    onToggleVisibleZoneType,
    isFullScreen,
    toggleFullScreen,
    escalation,
    scanEscalationState,
    onResolveEscalation,
    resolution,
    showResolveAction,
    addTemporaryResolvedState,
    getDisplayStatus,
    cameraNames,
    siblingEscalations,
    aiOperatorRep,
    product,
    timestamp,
    onAlarmResolve,
    onCloseAlarm,
    showCloseAlarmButton,
    abTestConfig,
  } = props;

  const track = useAlarmGroupTracking();
  const [isStatusEditing, setIsStatusEditing] = useState(false);
  const canUpdateStatus = useCanUpdateAlarmGroupStatus();
  const user = useUser();
  const { dataVersion } = useAlarmGroupStatic();
  const { data: tenantDetails } = useTenantDetails(user.tenant.id);
  const resolveAlarmGroupMutation = useUpdateAlarmGroupState(
    alarmGroupId,
    () => {
      toast('Alarm group resolved successfully', { type: 'success' });
      setIsStatusEditing(false);
      if (addTemporaryResolvedState) {
        addTemporaryResolvedState(alarmGroupId);
      }
    }
  );
  const resolveLocationAlarmMutation = useUpdateLocationAlarm(alarmGroupId, {
    onSuccess: () => {
      setIsStatusEditing(false);
      toast('Alarm resolved successfully', { type: 'success' });
      onAlarmResolve?.(StatusType.RESOLVED);
    },
    dataVersion,
  });

  const onCopyLink = async () => {
    track('alarm_group_copy_link');
    const config = getProductConfig(product);
    // Scan links never carry a version: scan always reads the legacy dataset.
    const locationAlarmLink = appendVersionParam(
      `${window.location.origin}/${config.urlPath}/${alarmGroupId}`,
      product === UnifiedMonitoringProduct.SCAN ? undefined : dataVersion
    );
    try {
      await navigator.clipboard.writeText(locationAlarmLink);
      toast(config.linkCopiedMessage);
    } catch {
      Sentry.captureMessage('Error copying text to clipboard');
    }
  };

  const onOpenInferenceServerABTest = () => {
    const config = getProductConfig(product);
    const locationAlarmLink = `${window.location.origin}/${config.urlPath}/${alarmGroupId}?abTest=inference_server`;
    window.open(locationAlarmLink, '_blank');
  };

  const handleResolveClick = () => {
    setIsStatusEditing(true);
  };
  const onClose = () => {
    setIsStatusEditing(false);
  };

  const onSubmit = (comment: string) => {
    if (
      product === UnifiedMonitoringProduct.SCAN ||
      product === UnifiedMonitoringProduct.REMOTE_GUARDING
    ) {
      track('alarm_group_resolve_submit');
    }
    if (product === UnifiedMonitoringProduct.SCAN) {
      const displayStatus = getDisplayStatus
        ? getDisplayStatus(alarmGroupId, alarmStatus)
        : alarmStatus;
      const resolutionType =
        displayStatus.toLowerCase() === 'in_progress'
          ? ('escalation_close' as const)
          : ('safe' as const);

      const payload = {
        resolution: resolutionType,
        resolutionComment: comment,
        operatorId: user.email,
      };
      resolveAlarmGroupMutation.mutate(payload);
    } else if (
      product === UnifiedMonitoringProduct.REMOTE_GUARDING ||
      product === UnifiedMonitoringProduct.ENTERPRISE
    ) {
      resolveLocationAlarmMutation.mutate({
        status: StatusType.RESOLVED,
        comment,
      });
    }
  };
  const displayStatus = getDisplayStatus
    ? getDisplayStatus(alarmGroupId, alarmStatus)
    : alarmStatus;
  const isResolved = RESOLVED_STATUSES.includes(displayStatus.toLowerCase());
  const showResolveButton = !isResolved && canUpdateStatus && showResolveAction;
  const [openShareAlarm, setOpenShareAlarm] = useState(false);
  const onShare = () => {
    track('alarm_group_share');
    setOpenShareAlarm(true);
  };

  const statusType = mapAlarmGroupStatusToLabelType(displayStatus);

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1">
      <div
        id="alarm-header"
        className="@container relative top-0 flex h-16 items-center gap-3 px-2 py-2"
      >
        {showCloseAlarmButton && (
          <Button variant="icon" onClick={onCloseAlarm}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        )}

        {isStatusEditing && (
          <div className="relative">
            <StatusStepper
              onSubmit={onSubmit}
              onClose={onClose}
              isSubmitInProgress={
                resolveAlarmGroupMutation.isLoading ||
                resolveLocationAlarmMutation.isLoading
              }
              useAlternativeOptions={
                tenantDetails?.config?.useAlternativeResolutionOptions
              }
            />
          </div>
        )}
        {!isStatusEditing && (
          <div className="flex max-w-[50%] flex-col overflow-hidden">
            <span
              className="cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
              title={`#${alarmGroupId} - ${location?.name}`}
              onClick={(e) => {
                const url = `/locations/${location.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              {location.name}
            </span>
            <div className="relative flex flex-1 items-center gap-4">
              <span
                className={clsx(
                  'text-xs',
                  statusType === 'success' && 'text-status-green',
                  statusType === 'warning' && 'text-status-yellow',
                  statusType === 'error' && 'text-status-red'
                )}
              >
                {normalizeAlarmGroupStatus(displayStatus)}
              </span>
            </div>
          </div>
        )}
        {!isStatusEditing && (
          <>
            <div className="@lg:flex hidden flex-1 flex-col items-center justify-center">
              {location.timezone && <LiveClock timezone={location.timezone} />}

              <span className="dark:text-ondark-text-2 text-onlight-text-2 text-xs">
                <MonitoringWindowInfo locationId={location.id} />
              </span>
            </div>
            <div className="@lg:hidden dark:text-ondark-text-2 text-onlight-text-2 flex flex-1 flex-col items-center justify-center space-y-1">
              {/* Row 1: Progressive Live Clock with icon */}
              <div className="flex items-center gap-1.5">
                <ClockIcon className="h-4 w-4 flex-shrink-0" />
                <div className="font-mono text-xs font-medium">
                  {location.timezone && (
                    <ProgressiveLiveClock timezone={location.timezone} />
                  )}
                </div>
              </div>

              {/* Row 2: Progressive Monitoring Window with icon */}
              <div className="flex items-center gap-1.5">
                <MonitoringIcon className="h-4 w-4 flex-shrink-0" />
                <div className="text-center font-mono text-xs">
                  <ProgressiveMonitoringWindow locationId={location.id} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HeaderActions
                mode={globalCamMode}
                onChangeMode={onChangeGlobalCamMode}
                locationName={location.name}
              />
              <ZoneOverlayDropdown
                visibleZoneTypes={visibleZoneTypes}
                onToggleVisibleZoneType={onToggleVisibleZoneType}
              />
              {showResolveButton && (
                <Button
                  title="Resolve"
                  variant="success"
                  onClick={handleResolveClick}
                >
                  Resolve
                </Button>
              )}
              <MenuActions
                isFullScreen={isFullScreen}
                onCopyLink={onCopyLink}
                toggleFullScreen={toggleFullScreen}
                onShare={onShare}
                abTestConfig={abTestConfig}
                onOpenInferenceServerABTest={onOpenInferenceServerABTest}
              />
            </div>
          </>
        )}
      </div>
      <EscalationBannerContainer
        product={product}
        alarmGroupId={alarmGroupId}
        location={location}
        escalation={escalation}
        scanEscalationState={scanEscalationState}
        resolution={resolution}
        siblingEscalations={siblingEscalations}
        dataVersion={dataVersion}
        onResolveEscalation={onResolveEscalation}
        addTemporaryResolvedState={addTemporaryResolvedState}
        cameraNames={cameraNames}
        aiOperatorRep={aiOperatorRep}
      />
      {openShareAlarm && (
        <ShareResource
          resourceId={alarmGroupId}
          resourceType={mapProductToResourceType(product)}
          onClose={() => setOpenShareAlarm(false)}
          timestamp={timestamp}
          dataVersion={dataVersion}
        />
      )}
    </div>
  );
}

export default AlarmGroupHeader;
